"use client";
import React, { useState } from "react";
import SecondaryButton from "@/components/ui/secondary-button";
import { Phone } from "@/components/icons";
import Banner from "@/logos/banner";
import DesktopNavigationItem from "@/components/ui/navigation/desktop-navigation-item";
import Link from "next/link";
import { CALLRAIL_HREF, CALLRAIL_PRETTY, cn } from "@/lib/utils";
import { useWindowSize } from "@/hooks/use-window-size";
import { NavItem } from "@/lib/data";
import { LINKS_NAVITEMS } from "@/lib/links";
import { usePathname } from "next/navigation";

export interface DesktopNavigationProps {
  className?: string;
  links?: NavItem[];
}

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  links,
  className,
}) => {
  const desktopNavItems = links ?? LINKS_NAVITEMS;
  const [menuOpen, setMenuOpen] = useState<string>("");
  const size = useWindowSize();
  const path = usePathname();

  const flatList = React.useMemo(
    () =>
      desktopNavItems.flatMap((navItem) => [
        navItem,
        ...(navItem.subMenu || []),
        ...(navItem.subMenu?.flatMap((subItem) => subItem.subMenu || []) || []),
      ]),
    [desktopNavItems]
  );

  return (
    <nav
      className={cn(
        "w-full border-b border-neutral-300 bg-white",
        className ?? ""
      )}
    >
      <div className="mx-auto flex h-20 w-full max-w-lg flex-row items-center justify-between lg:px-3">
        <Link
          href="/"
          prefetch
        >
          <Banner />
        </Link>
        {
          <ul className="flex flex-col pr-2 lg:flex-row lg:gap-4">
            {desktopNavItems.map((item) => {
              const currentItem = flatList.find(
                (navItem) => path.replace(/\/$/, "") === navItem.href
              );

              let isPartOfNest = false;

              if (currentItem) {
                const currentNest = currentItem.nest;
                const parentNest = currentNest.split("/").at(0);

                isPartOfNest =
                  item.nest === parentNest ||
                  path.replace(/\/$/, "") === item.href;
              }

              return (
                <DesktopNavigationItem
                  key={item.nest + "-" + item.href}
                  href={item.href}
                  displayName={item.displayName}
                  subMenu={item.subMenu}
                  nest={item.nest}
                  isPartOfNest={isPartOfNest}
                  menuOpen={menuOpen}
                  setMenuOpen={setMenuOpen}
                />
              );
            })}
          </ul>
        }

        <SecondaryButton
          className="chromatic-ignore flex !h-fit flex-row items-center gap-2"
          size={size === "sm" ? "sm" : "md"}
          color="orange"
          asChild
        >
          <a
            href={CALLRAIL_HREF}
            id="hotline-desktop-navbar"
          >
            <Phone className="size-3 md:size-4" />
            {CALLRAIL_PRETTY}
          </a>
        </SecondaryButton>
      </div>
    </nav>
  );
};

export default DesktopNavigation;
